import React, { memo, useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// SVG icons for field types
const FIELD_TYPE_ICONS = {
  text: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7V4h16v3" />
      <path d="M9 20h6" />
      <path d="M12 4v16" />
    </svg>
  ),
  number: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 17l6-6-6-6" />
      <path d="M12 19h8" />
    </svg>
  ),
  checkbox: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  select: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
  date: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4" />
      <path d="M8 2v4" />
      <path d="M3 10h18" />
    </svg>
  ),
  url: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
};

/**
 * SortableFieldRow Component
 * 
 * Individual field row with drag handle for reordering.
 * Supports inline editing, type selection, and options management for select fields.
 * 
 * PERFORMANCE: Uses local state for inputs - only syncs on blur
 */
const SortableFieldRow = memo(({ 
  field, 
  index, 
  totalFields,
  fieldTypes, 
  isNew, 
  newFieldInputRef,
  onFieldNameCommit,
  onFieldNameKeyDown,
  onFieldTypeChange,
  onDeleteField,
  onToggleExpanded,
  isExpanded,
  onAddOption,
  onUpdateOption,
  onDeleteOption,
}) => {
  // PERFORMANCE: Local state for instant typing - only sync on blur
  const [localName, setLocalName] = useState(field.name);
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const typeButtonRef = useRef(null);
  const typeMenuRef = useRef(null);
  
  // Sync local state when field.name changes externally
  useEffect(() => {
    setLocalName(field.name);
  }, [field.name]);

  // Calculate menu position when opening
  const updateMenuPosition = useCallback(() => {
    if (typeButtonRef.current) {
      const rect = typeButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
  }, []);

  // Close type menu when clicking outside
  useEffect(() => {
    if (!showTypeMenu) return;
    const handleClickOutside = (e) => {
      if (typeButtonRef.current?.contains(e.target)) return;
      if (typeMenuRef.current?.contains(e.target)) return;
      setShowTypeMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [showTypeMenu]);

  // Update position on scroll/resize while menu is open
  useEffect(() => {
    if (!showTypeMenu) return;
    updateMenuPosition();
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);
    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [showTypeMenu, updateMenuPosition]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const fieldType = fieldTypes.find(t => t.id === field.type);
  
  const handleNameBlur = () => {
    onFieldNameCommit(field.id, localName);
  };
  
  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      onFieldNameKeyDown(e, field.id, localName);
    }
  };

  const handleTypeSelect = (typeId) => {
    onFieldTypeChange(field.id, typeId);
    setShowTypeMenu(false);
  };

  const handleToggleTypeMenu = () => {
    if (!showTypeMenu) {
      updateMenuPosition();
    }
    setShowTypeMenu(!showTypeMenu);
  };

  // Prevent drag activation on interactive elements
  const stopDrag = (e) => e.stopPropagation();

  return (
    <div 
      ref={setNodeRef} 
      style={style}
      className={`custom-fields-manager__field ${isExpanded ? 'custom-fields-manager__field--expanded' : ''} ${isDragging ? 'custom-fields-manager__field--dragging' : ''}`}
      {...attributes}
      {...listeners}
    >
      {/* Main row */}
      <div className="custom-fields-manager__field-row">
        {/* Name field with label */}
        <div className="custom-fields-manager__field-group custom-fields-manager__field-group--name">
          <span className="custom-fields-manager__field-label">NAME:</span>
          <div className="custom-fields-manager__field-name-wrapper">
            {/* Hint shown when new and empty */}
            {isNew && !localName && (
              <span className="custom-fields-manager__field-hint">
                💡 Type a field name...
              </span>
            )}
            <input
              ref={isNew ? newFieldInputRef : null}
              type="text"
              className="custom-fields-manager__field-name-input"
              value={localName}
              onChange={(e) => setLocalName(e.target.value)}
              onBlur={handleNameBlur}
              onKeyDown={handleNameKeyDown}
              onPointerDown={stopDrag}
              placeholder={isNew ? '' : 'Field name'}
            />
          </div>
        </div>

        {/* Type selector with label - custom dropdown */}
        <div className="custom-fields-manager__field-group custom-fields-manager__field-group--type">
          <span className="custom-fields-manager__field-label">TYPE:</span>
          <div className="custom-fields-manager__type-dropdown">
            <button
              ref={typeButtonRef}
              className="custom-fields-manager__type-btn"
              onClick={handleToggleTypeMenu}
              onPointerDown={stopDrag}
            >
              <span className="custom-fields-manager__type-icon">
                {FIELD_TYPE_ICONS[field.type]}
              </span>
              <span className="custom-fields-manager__type-label">{fieldType?.label}</span>
              <span className="custom-fields-manager__type-arrow">▾</span>
            </button>
            
            {showTypeMenu && createPortal(
              <div 
                ref={typeMenuRef} 
                className="custom-fields-manager__type-menu"
                style={{
                  position: 'fixed',
                  top: menuPosition.top,
                  left: menuPosition.left,
                }}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {fieldTypes.map(type => (
                  <button
                    key={type.id}
                    className={`custom-fields-manager__type-option ${type.id === field.type ? 'custom-fields-manager__type-option--active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTypeSelect(type.id);
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <span className="custom-fields-manager__type-option-icon">
                      {FIELD_TYPE_ICONS[type.id]}
                    </span>
                    <span className="custom-fields-manager__type-option-label">{type.label}</span>
                  </button>
                ))}
              </div>,
              document.body
            )}
          </div>
        </div>

        <div className="custom-fields-manager__field-actions">
          {field.type === 'select' && (
            <button
              className="custom-fields-manager__field-btn custom-fields-manager__field-btn--edit"
              onClick={() => onToggleExpanded(field.id)}
              onPointerDown={stopDrag}
              title="Edit dropdown options"
            >
              EDIT OPTIONS
            </button>
          )}
          <button
            className="custom-fields-manager__field-btn custom-fields-manager__field-btn--delete"
            onClick={() => onDeleteField(field.id)}
            onPointerDown={stopDrag}
            title="Delete field"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
        </div>
      </div>

      {/* Options editor (expanded for select type) */}
      {isExpanded && field.type === 'select' && (
        <div className="custom-fields-manager__options-panel">
          <div className="custom-fields-manager__options-label">Dropdown Options</div>
          <div className="custom-fields-manager__options">
            {(field.options || []).map(option => (
              <div key={option.id} className="custom-fields-manager__option">
                <div 
                  className="custom-fields-manager__option-color"
                  style={{ backgroundColor: option.color }}
                />
                <input
                  type="text"
                  className="custom-fields-manager__option-input"
                  value={option.label}
                  onChange={(e) => onUpdateOption(field.id, option.id, { label: e.target.value })}
                  onPointerDown={stopDrag}
                  placeholder="Option label"
                />
                <button
                  className="custom-fields-manager__option-delete"
                  onClick={() => onDeleteOption(field.id, option.id)}
                  onPointerDown={stopDrag}
                  disabled={(field.options?.length || 0) <= 1}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              className="custom-fields-manager__add-option-btn"
              onClick={() => onAddOption(field.id)}
              onPointerDown={stopDrag}
            >
              + Add Option
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

SortableFieldRow.displayName = 'SortableFieldRow';

export default SortableFieldRow;
